import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Mail, MessageSquare, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Self-contained email/SMS template manager, shared by Event Rules and Analytics > Alerts.
// Templates live in a single library (/event-notification-templates) so a template
// created in one surface is reusable in the other.
export const NotificationTemplateModal = ({ open, onClose, api, initialType = 'email', onTemplatesChanged }) => {
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [templateForm, setTemplateForm] = useState({ name: '', template_type: initialType, subject: '', body: '' });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/event-notification-templates');
      setTemplates(res.data.templates || []);
      setPlaceholders(res.data.placeholders || []);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => {
    if (open) {
      setTemplateForm({ name: '', template_type: initialType, subject: '', body: '' });
      setEditingTemplate(null);
      fetchTemplates();
    }
  }, [open, initialType, fetchTemplates]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-mgmt-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#2563eb]" />Notification Templates</DialogTitle>
          <DialogDescription>Create and manage email/SMS templates. Templates are shared across all your event rules and alerts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {templates.length > 0 && !editingTemplate && (
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {templates.map(t => (
                <div key={t.id} className="flex items-center justify-between p-2 rounded-md border bg-muted/30">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {t.template_type === 'email' ? <Mail className="w-4 h-4 text-blue-500 shrink-0" /> : <MessageSquare className="w-4 h-4 text-green-500 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{t.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{t.template_type === 'email' ? `Subject: ${t.subject || '(no subject)'}` : `${(t.body || '').slice(0, 60)}...`}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" title="Edit" onClick={() => { setEditingTemplate(t); setTemplateForm({ name: t.name, template_type: t.template_type, subject: t.subject || '', body: t.body || '' }); }}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" title="Delete" onClick={async () => { try { await api.delete(`/event-notification-templates/${t.id}`); toast.success('Template deleted'); fetchTemplates(); onTemplatesChanged && onTemplatesChanged(); } catch { toast.error('Failed to delete'); } }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-3 p-3 rounded-lg border">
            <p className="text-sm font-medium">{editingTemplate ? 'Edit Template' : 'New Template'}</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input value={templateForm.name} onChange={(e) => setTemplateForm(p => ({ ...p, name: e.target.value }))} placeholder="My Custom Template" className="h-8 text-sm" data-testid="tpl-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select value={templateForm.template_type} onValueChange={(v) => setTemplateForm(p => ({ ...p, template_type: v }))} disabled={!!editingTemplate}>
                  <SelectTrigger className="h-8 text-sm" data-testid="tpl-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {templateForm.template_type === 'email' && (
              <div className="space-y-1">
                <Label className="text-xs">Subject Line</Label>
                <Input value={templateForm.subject} onChange={(e) => setTemplateForm(p => ({ ...p, subject: e.target.value }))} placeholder="Alert: {rule_name} on {device_name}" className="h-8 text-sm" data-testid="tpl-subject" id="tpl-subject-input"
                  onFocus={() => { window._tplLastField = 'subject'; }}
                  onSelect={(e) => { window._tplCursorPos = e.target.selectionStart; }}
                />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Body</Label>
              <textarea value={templateForm.body} onChange={(e) => setTemplateForm(p => ({ ...p, body: e.target.value }))} placeholder={templateForm.template_type === 'email' ? 'Device {device_name} triggered {rule_name}.\n\nDetails: {event_message}\nTime: {event_time} ({timezone})\nLocation: {map_link}' : '{device_name}\n{event_message}\n{event_time} ({timezone})\n{map_link}'} className="w-full h-24 text-sm p-2 border rounded-md bg-background resize-y" data-testid="tpl-body" id="tpl-body-input"
                onFocus={() => { window._tplLastField = 'body'; }}
                onSelect={(e) => { window._tplCursorPos = e.target.selectionStart; }}
              />
              {templateForm.template_type === 'sms' && (
                <p className={`text-[10px] ${(templateForm.body || '').length > 160 ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                  {(templateForm.body || '').length}/160 characters {(templateForm.body || '').length > 160 ? '— SMS may be split into multiple messages' : ''}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium">Available Placeholders: <span className="text-muted-foreground font-normal">(click to insert at cursor)</span></p>
              <div className="flex flex-wrap gap-1">
                {placeholders.map(p => (
                  <button key={p.key} type="button" className="px-1.5 py-0.5 text-[10px] rounded bg-muted border hover:bg-primary/10 font-mono" title={p.description}
                    onClick={() => {
                      const field = window._tplLastField || 'body';
                      const pos = window._tplCursorPos ?? null;
                      setTemplateForm(prev => {
                        const currentVal = prev[field] || '';
                        const insertPos = (pos !== null && pos <= currentVal.length) ? pos : currentVal.length;
                        const newVal = currentVal.slice(0, insertPos) + p.key + currentVal.slice(insertPos);
                        window._tplCursorPos = insertPos + p.key.length;
                        return { ...prev, [field]: newVal };
                      });
                      setTimeout(() => {
                        const el = document.getElementById(field === 'subject' ? 'tpl-subject-input' : 'tpl-body-input');
                        if (el) { el.focus(); const newPos = window._tplCursorPos || 0; el.setSelectionRange(newPos, newPos); }
                      }, 10);
                    }}
                  >{p.key}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={saving} onClick={async () => {
                if (!templateForm.name.trim()) { toast.error('Template name is required'); return; }
                if (!templateForm.body.trim()) { toast.error('Template body is required'); return; }
                setSaving(true);
                try {
                  if (editingTemplate) {
                    await api.put(`/event-notification-templates/${editingTemplate.id}`, { name: templateForm.name, subject: templateForm.subject, body: templateForm.body });
                    toast.success('Template updated');
                  } else {
                    await api.post('/event-notification-templates', templateForm);
                    toast.success('Template created');
                  }
                  fetchTemplates();
                  onTemplatesChanged && onTemplatesChanged();
                  setEditingTemplate(null);
                  setTemplateForm({ name: '', template_type: templateForm.template_type, subject: '', body: '' });
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save template'); }
                finally { setSaving(false); }
              }} data-testid="tpl-save-btn">
                {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
              {editingTemplate && (
                <Button variant="outline" size="sm" onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', template_type: templateForm.template_type, subject: '', body: '' }); }}>Cancel</Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationTemplateModal;
